import React, { useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  makeStyles,
  FormControl,
  TextField,
  Select,
  InputLabel,
  FormHelperText,
} from '@material-ui/core';
import { useSnackbar } from 'notistack';

import {
  selectBrandImage,
  selectBrandDescription,
} from 'selectors/brandRegistration';
import {
  createRegistration,
  updateRegistration,
  createRegistrationAndUploadImage,
  updateRegistrationAndUploadImage,
} from 'modules/brandRegistration';
import { COUNTIES } from 'util/APIUtils';
import { scrollToEl } from 'util/helpers';
import FormValidator from 'components/formValidator';
import BrandDecorator from './BrandDecorator';
import { BRAND_DESCRIPTOR } from '../constants';

const useStyles = makeStyles(() => ({
  wrapper: {
    textAlign: 'left',
    maxWidth: '800px',
    width: '100%',
    margin: 'auto',
  },
  panelHeader: {
    backgroundColor: '#f1f1f1',
    '& .MuiExpansionPanelSummary-content': {
      margin: '5px 0',
    },
  },
  title: {
    margin: '0',
    fontWeight: '400',
    fontSize: '18px',
  },
  content: {
    padding: '15px',
    width: '100%',
  },
  form: {
    textAlign: 'left',
    width: '100%',
    '& .MuiFormControl-root': {
      margin: '0 0 5px',
      width: '100%',
      maxWidth: '600px',
    },
  },
}));

const BrandSelection = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const brandDescription = useSelector(selectBrandDescription);
  const formRef = useRef(null);
  const sketchRef = useRef();
  const { enqueueSnackbar } = useSnackbar();
  const brandImage = useSelector(selectBrandImage);
  const [image, setImage] = useState(brandImage);

  const handleFormSubmit = (ev, { errors, formData }) => {
    ev.preventDefault();

    const sketchObj = sketchRef?.current?.toJSON() || false;

    if (!errors) {
      if (!sketchObj && image) {
        if (brandDescription.id) {
          dispatch(updateRegistration(formData, image));
        } else {
          dispatch(createRegistration(formData, image));
        }
      } else if (sketchObj && sketchObj.objects.length > 0) {
        sketchRef.current._canvas.toBlob((blob) => {
          const date = new Date();
          const file = new File(
            [blob],
            `${String(date.getTime())}_brand`,
            {
              type:"image/png",
              lastModified: date,
            },
          );

          if (brandDescription.id) {
            dispatch(updateRegistrationAndUploadImage(formData, file));
          } else {
            dispatch(createRegistrationAndUploadImage(formData, file));
          }
        }, 'image/png');
      } else {
        enqueueSnackbar(
          'No selected brand image',
          { variant: 'error' },
        );
      }
    } else {
      const firstError = Object.keys(errors)[0];
      const field = document.querySelector(`#${firstError}`);

      if (field) {
        scrollToEl(field);
        field.focus();
      }
    }
  };

  const handleNextStep = () => {
    formRef.current.dispatchEvent(
      new Event(
        'submit',
        { bubbles: true, cancelable: true },
      )
    );
  };

  const handleBrandImageSelection = (image) => {
    setImage(image);
  };

  return (
    <div className={classes.wrapper}>
      <FormValidator
        initialData={{
          description: brandDescription?.description || '',
          county: brandDescription?.county || '',
          id: brandDescription.id,
        }}
        descriptor={BRAND_DESCRIPTOR}
        onSubmit={handleFormSubmit}
      >
        {({ formData, setFormValue, handleSubmit, errors = {} }) => {
          return (
            <form
              className={classes.form}
              onSubmit={handleSubmit}
              ref={formRef}
            >
              <h3>Please Describe Your Brand</h3>
              <p>1. Add a description that people might use to find your brand in common language.</p>
              <TextField
                value={formData.description}
                variant="outlined"
                size="small"
                label="Description"
                onChange={(ev) => setFormValue('description', ev.target.value)}
                error={!!errors.description}
                helperText={errors.description}
                id="description"
              />
              <p>2. What county will your brand be used in?</p>
              <FormControl variant="outlined" size="small" error={!!errors.county}>
                <InputLabel htmlFor="county">County</InputLabel>
                <Select id="county" native label="County" value={formData.county} onChange={(ev) => setFormValue('county', ev.target.value)}>
                  <option aria-label="None" value="" />
                  {COUNTIES.map((county) =>
                    <option key={county} value={county}>{county}</option>
                  )}
                </Select>
                {!!errors.county && <FormHelperText>{errors.county}</FormHelperText>}
              </FormControl>
            </form>
          );
        }}
      </FormValidator>
      <p>3. Find, create or upload an image of your brand.</p>
      <BrandDecorator
        selectedImage={image}
        handleBrandImageSelection={handleBrandImageSelection}
        handleNextStep={handleNextStep}
        sketchRef={sketchRef}
      />
    </div>
  );
};

export default BrandSelection;

